import type { McpResponseModel } from '@/customApiClients/mgmtApiClient/Api';
import { cn } from '@/lib/utils';

import SourceTypeIcon from '@/components/icons/SourceTypeIcon';

export interface SourceCardProps {
    source: McpResponseModel;
    isSelected: boolean;
    disabled?: boolean;
    onToggle: (source: McpResponseModel) => void;
}

export default function SourceCard({
    source,
    isSelected,
    disabled = false,
    onToggle
}: SourceCardProps) {
    const description = source.description || '';

    return (
        <button
            type="button"
            aria-pressed={isSelected}
            onClick={(e) => {
                e.stopPropagation();
                onToggle(source);
            }}
            disabled={disabled}
            className={cn(
                'group flex items-center gap-3 rounded-lg w-full',
                'pl-4 pr-6 py-3',
                'text-left outline-hidden transition-all duration-200',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                'shadow-xs',
                'col-span-1',
                'h-24',
                isSelected && [
                    'bg-selected dark:bg-white/5',
                    'border border-foreground/80 dark:border-white/20'
                ],
                !isSelected && [
                    'bg-background',
                    'border border-border',
                    'hover:border-muted-foreground/50',
                    'hover:shadow-md/50'
                ],
                disabled && 'opacity-50 cursor-not-allowed'
            )}
        >
            {/* Icon Container */}
            <div
                className={cn(
                    'flex shrink-0 justify-center items-center',
                    'size-11 rounded-lg p-2',
                    isSelected
                        ? 'bg-background border border-icon '
                        : 'bg-background border border-border'
                )}
            >
                <SourceTypeIcon
                    configType={source.configType}
                    className="size-6"
                />
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 gap-0 min-w-0">
                <span
                    className={
                        'font-medium text-base text-foreground line-clamp-1 '
                    }
                >
                    {source.displayName}
                </span>
                {description && (
                    <span
                        className="text-foreground-secondary text-sm line-clamp-2"
                        title={description}
                    >
                        {description}
                    </span>
                )}
            </div>
        </button>
    );
}
